import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Send, X, Download } from "lucide-react";
import QRCode from "qrcode";
import { useAuth } from "@/context/AuthContext";

export default function ShareModal({ vacancy, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [referrerCode, setReferrerCode] = useState("");
  const [refLoading, setRefLoading] = useState(!isAdmin);
  const [messageTemplate, setMessageTemplate] = useState("");

  useEffect(() => {
    // Admin shares the public link WITHOUT ref attribution (they are not a referrer)
    api.get("/config").then(({ data }) => setMessageTemplate(data?.share_message_template || "")).catch(() => {});
    if (isAdmin) {
      return;
    }
    // Fetch referrer to embed their code in the candidate-facing share URL
    api.get("/referrers/me")
      .then(({ data }) => { if (data?.code) setReferrerCode(data.code); })
      .catch(() => {})
      .finally(() => setRefLoading(false));
  }, [isAdmin]);

  const canShare = isAdmin || !!referrerCode;
  const shareUrl = isAdmin
    ? `${window.location.origin}/apply/${vacancy.share_code}`
    : (referrerCode ? `${window.location.origin}/apply/${vacancy.share_code}?ref=${referrerCode}` : "");

  const isContinuous = vacancy.service_type === "continuous";
  const priceLine = isContinuous
    ? `Remuneração média mensal: ${Number(vacancy.monthly_avg || 0).toFixed(0)} €/mês`
    : `Valor total do serviço: ${Number(vacancy.total_value || 0).toFixed(0)} €`;
  const serviceLine = isContinuous ? "Serviço de continuidade" : "Serviço pontual";

  const renderTemplate = (tpl) => (tpl || "")
    .replaceAll("{locality}", vacancy.locality || "")
    .replaceAll("{service_line}", serviceLine)
    .replaceAll("{price_line}", priceLine)
    .replaceAll("{schedule}", vacancy.schedule || "")
    .replaceAll("{apply_url}", shareUrl);

  const message = canShare && messageTemplate ? renderTemplate(messageTemplate) : "";

  useEffect(() => {
    if (!shareUrl) { setQrDataUrl(""); return; }
    QRCode.toDataURL(shareUrl, { width: 320, margin: 2, color: { dark: "#2C4C3B", light: "#F9F8F6" } })
      .then(setQrDataUrl)
      .catch(() => {});
  }, [shareUrl]);

  const logPublish = (platform, group_id) => {
    api.post("/publications", { vacancy_id: vacancy.id, group_id, platform }).catch(() => {});
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
      logPublish("copy");
    } catch { toast.error("Não foi possível copiar"); }
  };

  const shareNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `CARING — ${vacancy.title}`, text: message, url: shareUrl });
        logPublish("native");
      } else {
        await copy(message, "Mensagem");
      }
    } catch {}
  };

  const downloadQR = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `caring-${vacancy.share_code}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 flex items-center justify-between border-b border-[var(--border)] sticky top-0 bg-white z-10">
          <div>
            <div className="eyebrow">Partilhar com candidatos</div>
            <h2 className="font-heading text-2xl">{vacancy.title}</h2>
            {isAdmin && <div className="text-xs text-[var(--ink-soft)] mt-1">Partilha institucional (sem atribuição a referenciador)</div>}
            {!isAdmin && referrerCode && <div className="text-xs text-[var(--ink-soft)] mt-1">A sua recomendação: <span className="font-mono">{referrerCode}</span></div>}
          </div>
          <button onClick={onClose} className="pill-btn pill-ghost !py-2 !px-3" data-testid="close-share-modal"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-6">
          {refLoading ? (
            <div className="text-center text-[var(--ink-soft)] py-10" data-testid="share-loading">A preparar link com o seu código…</div>
          ) : !canShare ? (
            <div className="rounded-xl bg-[rgba(217,108,74,0.08)] border border-[rgba(217,108,74,0.3)] p-4 text-sm text-[var(--terracotta)]" data-testid="share-no-referrer">
              Não foi possível obter o seu código de referenciador. Complete o perfil e assine o protocolo para poder partilhar vagas com atribuição.
            </div>
          ) : (
            <>
          <div className="rounded-xl bg-[rgba(59,113,151,0.08)] border border-[rgba(59,113,151,0.2)] p-3 text-xs text-[var(--ink)]">
            {isAdmin
              ? "Este é o link público institucional da vaga (sem atribuição). Utilize para divulgação oficial CARING. Para atribuir candidaturas a um referenciador específico, essa partilha deve ser feita pelo próprio referenciador."
              : "A mensagem abaixo é dirigida ao candidato final: não menciona o seu prémio nem o portal do referenciador. Contém apenas o link público de candidatura, com o seu código embebido para que a candidatura seja atribuída corretamente."}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium">Mensagem para o candidato</label>
              <textarea readOnly value={message} className="mt-2 w-full h-56 p-3 rounded-xl bg-[var(--muted)] border border-[var(--border)] text-sm font-body" data-testid="share-message"/>
              <div className="mt-2 rounded-xl bg-[rgba(45,90,39,0.08)] border border-[rgba(45,90,39,0.15)] p-3 text-xs text-[var(--ink)]">
                <strong>Como usar:</strong> carregue em <em>Copiar mensagem</em> e cole no <strong>SMS</strong>, <strong>WhatsApp</strong>, <strong>Messenger</strong> ou qualquer outra aplicação de mensagens do seu telemóvel. O link do candidato já vai incluído com o seu código de referenciador.
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => copy(message, "Mensagem")} className="pill-btn pill-primary !py-2 !px-4" data-testid="copy-message-btn"><Copy className="w-4 h-4"/> Copiar mensagem</button>
                <button onClick={() => copy(shareUrl, "Link")} className="pill-btn pill-ghost !py-2 !px-4" data-testid="copy-link-btn"><Copy className="w-4 h-4"/> Copiar apenas o link</button>
                <button onClick={shareNative} className="pill-btn pill-ghost !py-2 !px-4" data-testid="native-share-btn"><Send className="w-4 h-4"/> Mais opções</button>
              </div>
            </div>
            <div className="text-center">
              <label className="text-sm font-medium block mb-2">QR Code (candidatos)</label>
              <div className="rounded-2xl bg-[var(--muted)] border border-[var(--border)] p-4 inline-block">
                {qrDataUrl ? <img src={qrDataUrl} alt="QR" className="w-56 h-56 mx-auto" data-testid="share-qr-code"/> : <div className="w-56 h-56 mx-auto bg-white"/>}
              </div>
              <button onClick={downloadQR} className="pill-btn pill-ghost !py-2 !px-4 mt-3" data-testid="download-qr-btn">
                <Download className="w-4 h-4"/> Descarregar QR
              </button>
              <div className="mt-3 text-xs text-[var(--ink-soft)] break-all" data-testid="share-url">{shareUrl}</div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
